// The console's tenant rules, kept pure so every screen and every test reads
// the same answer (spec 15 §3.2 state machine, §7 role fence).
//
// The server enforces all of this. The console mirrors it only to decide what
// to OFFER: a button the server will refuse is a button that teaches staff the
// console is broken. When the two disagree the server wins and the screen shows
// what it said.

import type { ChipVariant } from "../../../components/status-chip";
import { LOCALES, type Locale } from "../../../i18n-dictionaries";
import {
  PlatformError,
  type CreateTenantInput,
  type PlatformRole,
  type PlatformScope,
  type TenantDetail,
  type TenantSummary,
} from "./platform-api";

/** Every status the §3.2 machine can hold, in the order the filter offers them. */
export const TENANT_STATUSES = [
  "active",
  "provisioning",
  "provision_failed",
  "suspended",
  "pending_deletion",
  "exporting",
  "purging",
  "purged",
] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

export function isTenantStatus(value: unknown): value is TenantStatus {
  return typeof value === "string" && (TENANT_STATUSES as readonly string[]).includes(value);
}

export function statusVariant(status: string): ChipVariant {
  switch (status) {
    case "active":
      return "ok";
    case "provisioning":
      return "accent";
    case "provision_failed":
      return "bad";
    case "suspended":
    case "pending_deletion":
      return "warn";
    default:
      return "neutral";
  }
}

export const SUSPENSION_REASONS = [
  "billing_overdue",
  "compliance",
  "tenant_request",
  "security_incident",
] as const;
export type SuspensionReason = (typeof SUSPENSION_REASONS)[number];
export type SuspensionMode = "grace" | "immediate";

/** The regions create accepts (contract `PlatformRegion`). */
export const PLATFORM_REGIONS = [
  "ap-southeast-1",
  "eu-central-1",
  "me-central-1",
  "af-south-1",
] as const;

/** The create contract's pattern: 3–63 lowercase letters, digits and inner hyphens. */
export const SUBDOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/u;

/** What a typed subdomain becomes before it is checked: trimmed and lower-cased. */
export function normalizeSubdomain(raw: string): string {
  return raw.trim().toLowerCase();
}

export function canCreateTenant(role: PlatformRole): boolean {
  return role === "platform_admin";
}

export interface TenantActions {
  retry: boolean;
  suspend: boolean;
  /** The reasons this role may suspend for (empty when it may not suspend). */
  suspendReasons: readonly SuspensionReason[];
  suspendModes: readonly SuspensionMode[];
  restore: boolean;
  resendInvite: boolean;
}

const NO_ACTIONS: TenantActions = {
  retry: false,
  suspend: false,
  suspendReasons: [],
  suspendModes: [],
  restore: false,
  resendInvite: false,
};

/**
 * Which lifecycle buttons the S5 header offers. Built routes only: request and
 * cancel deletion have no API yet, so they are not offered at all.
 *
 *   retry        — provision_failed, platform_admin.
 *   suspend      — active; admin for any reason and mode, billing for
 *                  billing_overdue in grace only.
 *   restore      — suspended; admin always, billing when the suspension was
 *                  for billing_overdue.
 *   resendInvite — active; admin and support (the invite only works on a live
 *                  tenant, and provisioning sends the first one itself).
 *
 * A sandbox member (D-477) is offered none of them on a tenant that is not
 * sandbox: the server answers each with 403 STAFF_SCOPE_DENIED.
 */
export function tenantActions(
  role: PlatformRole,
  detail: TenantDetail,
  scope: PlatformScope,
): TenantActions {
  // D-477 ruling 3: a sandbox member writes about sandbox tenants only, so on
  // a fleet tenant it is offered nothing the server would refuse.
  if (scope === "sandbox" && !detail.tenant.sandbox) return NO_ACTIONS;
  const status = detail.tenant.status;
  const admin = role === "platform_admin";
  const billing = role === "platform_billing";
  const suspendReasons: readonly SuspensionReason[] =
    status !== "active" ? [] : admin ? SUSPENSION_REASONS : billing ? ["billing_overdue"] : [];
  const suspendModes: readonly SuspensionMode[] = admin
    ? ["grace", "immediate"]
    : billing
      ? ["grace"]
      : [];
  return {
    retry: admin && status === "provision_failed",
    suspend: suspendReasons.length > 0,
    suspendReasons,
    suspendModes: suspendReasons.length > 0 ? suspendModes : [],
    restore:
      status === "suspended" &&
      (admin || (billing && detail.suspension?.reason === "billing_overdue")),
    resendInvite: status === "active" && (admin || role === "platform_support"),
  };
}

/**
 * The tenant's own address, from the console's: `admin.<apex>` → `<sub>.<apex>`,
 * same scheme and port. Built from the host rather than configured, so a local
 * replica (`admin.localtest.me:8443`) and staging both link to themselves.
 */
export function tenantOrigin(subdomain: string, consoleLocation: Location | URL): string {
  const url = new URL(consoleLocation.href);
  const labels = url.hostname.split(".");
  const apex = labels.length > 1 ? labels.slice(1).join(".") : url.hostname;
  url.hostname = `${subdomain}.${apex}`;
  return url.origin;
}

/** Who a lifecycle row names: a staff member (by id), or the system. */
export type LifecycleActor = { kind: "staff"; staffId: string } | { kind: "system" };

export function lifecycleActor(actor: string): LifecycleActor {
  return actor.startsWith("staff:")
    ? { kind: "staff", staffId: actor.slice("staff:".length) }
    : { kind: "system" };
}

/** A staff directory read is admin + readonly only (spec 15 §0.1). */
export function canReadStaff(role: PlatformRole): boolean {
  return role === "platform_admin" || role === "platform_readonly";
}

/** The provisioning progress view stops polling once the tenant leaves provisioning. */
export function isProvisioningSettled(status: string): boolean {
  return status !== "provisioning";
}

/** A default language create accepts (the contract's `Locale`). */
export type TenantLocale = NonNullable<CreateTenantInput["default_locale"]>;

/**
 * The languages the wizard offers: every product locale, in registry order.
 * The create contract's `Locale` and the web's `LOCALES` both derive from the
 * one registry (packages/contracts/src/locales.ts), so the wizard cannot offer
 * a language the create refuses, and the annotation fails to compile if the
 * two ever part.
 */
export const TENANT_LOCALES: readonly (TenantLocale & Locale)[] = LOCALES;

/**
 * The currencies the wizard offers: the ISO 4217 codes the browser knows,
 * sorted, with USD first as the usual default. A tenant's currency is locked
 * once money-bearing rows exist (spec 16 §2.1), so a typo in a free-text box
 * would be permanent; a list removes the typo. The create checks the same list
 * on the server.
 */
export function tenantCurrencies(): readonly string[] {
  const supported = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf?.("currency");
  const list = supported !== undefined && supported.length > 0 ? [...supported].sort() : [];
  const rest = list.filter((c) => c !== "USD");
  return ["USD", ...rest];
}

/** What a create sent, for recognising its own tenant after a lost answer. */
export type CreateAttempt = Pick<CreateTenantInput, "name" | "subdomain" | "group_admin_email">;

/**
 * Whether a tenant found by subdomain is the one an earlier create made: same
 * subdomain, same name and the same admin email (case aside). Used only after
 * a create whose answer never arrived, when the next try is told the
 * subdomain is taken.
 */
export function isOwnCreate(tenant: TenantSummary, attempts: readonly CreateAttempt[]): boolean {
  return attempts.some(
    (a) =>
      tenant.subdomain === a.subdomain &&
      tenant.name.trim() === a.name.trim() &&
      tenant.group_admin_email.trim().toLowerCase() === a.group_admin_email.trim().toLowerCase(),
  );
}

/**
 * What a refused lifecycle write (suspend, restore, retry setup, resend
 * invite) means for the operator:
 *
 *   session — 401: the session ended; the screen sends them to sign in.
 *   scope   — 403 STAFF_SCOPE_DENIED: a sandbox member and a tenant that is
 *             not sandbox (D-477 ruling 3); the role is not the reason.
 *   role    — any other 403: this role may not do it (the server's fence won).
 *   changed — 409 on the tenant's state (INVALID_TENANT_STATUS,
 *             PROVISIONING_NOT_FAILED): someone else moved it first. The
 *             screen reloads the tenant so the header shows where it stands.
 *   failed  — anything else, including no answer at all; trying again is safe
 *             because the retry carries the same Idempotency-Key.
 */
export type ActionFailure = "session" | "scope" | "role" | "changed" | "failed";

export function actionFailure(error: unknown): ActionFailure {
  if (!(error instanceof PlatformError)) return "failed";
  if (error.status === 401) return "session";
  if (error.status === 403) return error.code === "STAFF_SCOPE_DENIED" ? "scope" : "role";
  if (
    error.status === 409 &&
    error.code !== "IDEMPOTENCY_IN_PROGRESS" &&
    error.code !== "IDEMPOTENCY_KEY_REUSE"
  ) {
    return "changed";
  }
  return "failed";
}
